import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Assignment,
  Person,
  SwapHoriz,
  AutoMode,
} from '@mui/icons-material';

const AssignmentHistory = ({ assignmentHistory = [], currentLead }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleString();
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      manager: 'warning',
      team_lead: 'info',
      bda: 'success'
    };
    return colors[role] || 'default';
  };

  const getAssignmentIcon = (assignment) => {
    if (assignment.reason?.includes('Auto-assigned') || assignment.reason?.includes('round-robin')) {
      return <AutoMode color="primary" />;
    }
    return <SwapHoriz color="secondary" />;
  };

  if (!assignmentHistory || assignmentHistory.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Assignment color="primary" />
            <Typography variant="h6">Assignment History</Typography>
          </Box>
          <Alert severity="info">
            No assignment history available for this lead.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Assignment color="primary" />
          <Typography variant="h6">Assignment History</Typography>
          <Chip label={`${assignmentHistory.length} assignments`} size="small" />
        </Box>

        <List>
          {assignmentHistory.map((assignment, index) => (
            <React.Fragment key={assignment.id || index}>
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ mt: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: index === 0 ? 'primary.main' : 'grey.500' }}>
                    {getAssignmentIcon(assignment)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ p: 2, bgcolor: index === 0 ? 'primary.50' : 'grey.50', borderRadius: 1 }}>
                      {/* Assignment Details */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {assignment.assigned_from ? 'Reassigned' : 'Initial Assignment'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(assignment.assigned_at)}
                          </Typography>
                        </Box>
                        {index === 0 && (
                          <Chip label="Current" color="primary" size="small" />
                        )}
                      </Box>

                      {/* Assignment Flow */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {assignment.assigned_from && (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24 }}>
                                <Person fontSize="small" />
                              </Avatar>
                              <Typography variant="body2">
                                {assignment.assigned_from || 'System'}
                              </Typography>
                            </Box>
                            <SwapHoriz color="action" />
                          </>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {assignment.assigned_to_name}
                            </Typography>
                            {/* Show role if available from current lead data */}
                            {index === 0 && currentLead?.assigned_to_role && (
                              <Chip
                                label={currentLead.assigned_to_role}
                                size="small"
                                color={getRoleColor(currentLead.assigned_to_role)}
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Assigned By */}
                      {assignment.assigned_by_name && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Assigned by: {assignment.assigned_by_name}
                          </Typography>
                        </Box>
                      )}

                      {/* Reason */}
                      {assignment.reason && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            "{assignment.reason}"
                          </Typography>
                        </Box>
                      )}

                      {/* Current Assignment Details */}
                      {index === 0 && currentLead && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Current Assignment Details:
                          </Typography>
                          {currentLead.assigned_to_full_name && (
                            <Typography variant="body2">
                              <strong>Full Name:</strong> {currentLead.assigned_to_full_name}
                            </Typography>
                          )}
                          {currentLead.assigned_to_email && (
                            <Typography variant="body2">
                              <strong>Email:</strong> {currentLead.assigned_to_email}
                            </Typography>
                          )}
                          {currentLead.assigned_to_phone && (
                            <Typography variant="body2">
                              <strong>Phone:</strong> {currentLead.assigned_to_phone}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < assignmentHistory.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default AssignmentHistory;